import { useCallback, useEffect, useRef, useState } from "react";
import {
	allAnswered,
	buildAllResults,
	createBatchState,
	goToQuestion,
	isLastQuestion,
	toggleChoice,
	setFreeText,
	updateCurrentChoice,
	FREE_TEXT_ID,
	type UserChoiceResult,
} from "./useUserChoice";
import { useChatStore } from "@/stores/useChatStore";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { useSubmitHITLToolResult } from "@/components/hitl/useSubmitHITLToolResult";
import { useToastStore } from "@/stores/useToastStore";
import { isImeConsumedKeyDown } from "@/lib/ime-composition";
import { ChoiceCard } from "@/components/hitl/choice-card/ChoiceCard";
import { ChoiceOptionButton } from "@/components/hitl/choice-card/ChoiceOptionButton";
import { useChoiceOptionFocus } from "@/components/hitl/choice-card/useChoiceOptionFocus";

const SKIP_RESULT = {
	selected_ids: [],
	free_text: null,
	skipped: true,
};

export default function UserChoicePrompt() {
	const hitlState = useChatStore((s) => s.hitlState);

	const active = hitlState?.kind === "user-choice";
	const questions = active ? hitlState.questions : undefined;

	const [batch, setBatch] = useState(() => createBatchState(questions ?? []));
	const rootRef = useRef<HTMLDivElement>(null);
	const autoSkippedRef = useRef(false);
	const { setOptionRef, focusByOffset } = useChoiceOptionFocus();

	const { submit: submitToolResult, submitting } = useSubmitHITLToolResult();

	const handleComplete = useCallback(
		(answers: UserChoiceResult[]) => {
			const result = answers.length === 1 ? answers[0] : { answers };
			void submitToolResult(result);
		},
		[submitToolResult],
	);

	const handleSkip = useCallback(() => {
		void submitToolResult(SKIP_RESULT);
	}, [submitToolResult]);

	const totalQ = batch.questions.length;
	const currentQ = batch.questions[batch.currentIndex];
	const state = batch.choices[batch.currentIndex];
	const last = isLastQuestion(batch);
	const finalSubmittable = last && allAnswered(batch);
	const selectedFreeText = state?.selectedIds.has(FREE_TEXT_ID) ?? false;
	const autoSkipMessage = active && totalQ === 0 ? "質問が空のためスキップしました。" : null;

	useEffect(() => {
		if (!autoSkipMessage || autoSkippedRef.current) return;

		autoSkippedRef.current = true;
		useToastStore.getState().show({
			type: "error",
			message: autoSkipMessage,
		});
		handleSkip();
	}, [autoSkipMessage, handleSkip]);

	// 質問が出た・変わったときや「その他」の入力欄が消えたときに、カードの外へ出たフォーカスを本体へ戻す。
	// カードの中の選択肢や入力欄にあるフォーカスは奪わない。押せなくなった「完了」からはフォーカスが外れるので戻す。
	useEffect(() => {
		if (!active || !currentQ) return;
		const focused = document.activeElement;
		if (rootRef.current?.contains(focused) && !focused?.matches(":disabled")) return;
		rootRef.current?.focus();
	}, [active, batch.currentIndex, currentQ, selectedFreeText]);

	const handleGoBack = useCallback(() => {
		setBatch((prev) => goToQuestion(prev, prev.currentIndex - 1));
	}, []);

	const handleGoNext = useCallback(() => {
		if (last) return;
		setBatch((prev) => goToQuestion(prev, prev.currentIndex + 1));
	}, [last]);

	const handleFinalSubmit = useCallback(() => {
		if (!allAnswered(batch)) return;
		handleComplete(buildAllResults(batch));
	}, [batch, handleComplete]);

	// 選ぶだけで、送信や次の質問への移動は Enter か「送信」「次へ」で行う。
	const handleChoiceClick = useCallback((choiceId: string) => {
		setBatch((prev) =>
			updateCurrentChoice(prev, (choiceState) => toggleChoice(choiceState, choiceId)),
		);
	}, []);

	const handleFreeText = useCallback((text: string) => {
		setBatch((prev) => updateCurrentChoice(prev, (choiceState) => setFreeText(choiceState, text)));
	}, []);

	const handleExplicitSubmit = useCallback(() => {
		if (last) {
			if (!finalSubmittable) return;
			handleFinalSubmit();
			return;
		}
		handleGoNext();
	}, [last, finalSubmittable, handleFinalSubmit, handleGoNext]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			// 変換中の Esc や Enter は IME のもの。
			if (isImeConsumedKeyDown(e.nativeEvent)) return;

			const target = e.target;
			const isButton = target instanceof HTMLButtonElement;
			const isTextInput =
				target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

			if (e.key === "Escape") {
				e.preventDefault();
				handleSkip();
				return;
			}

			if (e.key === "Enter" && !e.shiftKey) {
				// 押しっぱなしで質問を飛ばしたり送ったりしないよう、リピートは無視する。
				if (e.repeat) {
					e.preventDefault();
					return;
				}
				// 選択済みの選択肢・カード本体・「その他」の入力欄の Enter は送信。未選択の選択肢と「スキップ」などはボタンとして押す。
				const onSelectedOption =
					target instanceof HTMLElement && target.dataset.selected === "true";
				if (!isButton || onSelectedOption) {
					e.preventDefault();
					handleExplicitSubmit();
				}
				return;
			}

			if (isTextInput) return;

			if (e.key === "ArrowLeft" && totalQ > 1) {
				e.preventDefault();
				handleGoBack();
				return;
			}

			if (e.key === "ArrowRight" && totalQ > 1 && !last) {
				e.preventDefault();
				handleGoNext();
				return;
			}

			if (e.key === "ArrowDown") {
				e.preventDefault();
				focusByOffset(1);
				return;
			}

			if (e.key === "ArrowUp") {
				e.preventDefault();
				focusByOffset(-1);
			}
		},
		[totalQ, last, handleSkip, handleGoBack, handleGoNext, focusByOffset, handleExplicitSubmit],
	);

	if (!active || !currentQ || !state) return null;

	const indicatorKind = state.multiSelect ? "checkbox" : "radio";
	const primaryActionLabel = totalQ > 1 ? (last ? "完了" : "次へ") : "送信";
	const primaryActionDisabled = last ? !finalSubmittable : false;
	const primaryActionTestId = last ? "user-choice-submit" : "user-choice-next";
	const hint = [
		totalQ > 1 ? "←/→で質問を移動" : null,
		"↑/↓で選択肢を移動",
		"Spaceで選択",
		`Enterで${primaryActionLabel}`,
		"Escでスキップ",
	]
		.filter((part) => part !== null)
		.join(" · ");

	return (
		<ChoiceCard
			title={
				<>
					{currentQ.question}
					{state.multiSelect && <span className="font-normal text-fg-3">（複数選択可）</span>}
				</>
			}
			context={currentQ.context}
			progress={totalQ > 1 ? { current: batch.currentIndex + 1, total: totalQ } : undefined}
			onSkip={handleSkip}
			hint={hint}
			rootRef={rootRef}
			onKeyDown={handleKeyDown}
			busy={submitting}
			actions={
				<>
					{totalQ > 1 && batch.currentIndex > 0 && (
						<ConfirmButton
							label="戻る"
							variant="secondary"
							size="sm"
							loading={false}
							disabled={submitting}
							onClick={handleGoBack}
							data-testid="user-choice-back"
						/>
					)}
					<ConfirmButton
						label={primaryActionLabel}
						variant="neutral"
						size="sm"
						loading={false}
						disabled={primaryActionDisabled || submitting}
						onClick={handleExplicitSubmit}
						data-testid={primaryActionTestId}
					/>
					<ConfirmButton
						label="スキップ"
						variant="secondary"
						size="sm"
						loading={false}
						disabled={submitting}
						onClick={handleSkip}
					/>
				</>
			}
		>
			<div className="-mx-4 divide-y divide-line-1">
				{currentQ.choices.map((choice, idx) => (
					<ChoiceOptionButton
						key={choice.id}
						indicator={indicatorKind}
						label={choice.label}
						description={choice.description}
						selected={state.selectedIds.has(choice.id)}
						onClick={() => handleChoiceClick(choice.id)}
						buttonRef={setOptionRef(idx)}
					/>
				))}

				<div>
					<ChoiceOptionButton
						indicator={indicatorKind}
						label="その他"
						selected={selectedFreeText}
						expanded={selectedFreeText}
						onClick={() => handleChoiceClick(FREE_TEXT_ID)}
						buttonRef={setOptionRef(currentQ.choices.length)}
					/>
					{selectedFreeText && (
						<div className="flex pb-3 pl-11.5 pr-4">
							<input
								type="text"
								value={state.freeText}
								onChange={(e) => handleFreeText(e.target.value)}
								placeholder="回答を入力…"
								className="flex-1 rounded-3 bg-surface-1 px-3 py-2 text-ui text-fg-1 shadow-ring outline-none placeholder:text-fg-3 focus:shadow-focus disabled:opacity-40"
								autoFocus
							/>
						</div>
					)}
				</div>
			</div>
		</ChoiceCard>
	);
}
